import { createContext, useContext } from "react";
import { useHttpClient } from "../../lib/http";
import { TagMultiUpdateResponse } from "./domain/TagMultiUpdateResponse";
import { ApiTagRepository } from "./infrastructure/ApiTagRepository";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { TagAPIPromiseClient } from "../../gen/witbooking/backoffice/tags/v1/tag_api_grpc_web_pb";
import { ApiTagAssociationRepository } from "./infrastructure/ApiTagAssociationRepository";
import { AccommodationAPIPromiseClient } from "../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_grpc_web_pb";
import { ExtraAPIPromiseClient } from "../../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import { PackageAPIPromiseClient } from "../../gen/witbooking/backoffice/package/v1/package_api_grpc_web_pb";
import { TagAssociation } from "./domain/TagAssociation";
import { Tag } from "./domain/Tag";
import { useKeycloakAuthz } from "../../components/keycloak-provider";

export interface ContextState {
  getTags: () => Promise<Tag[]>;
  importTags: (propertyTicker: string, file: File) => Promise<TagMultiUpdateResponse>;
  getTagsAssociations: (propertyTicker: string) => Promise<TagAssociation[]>;
  updateTagAssociation: (
    oldTagAssociation: TagAssociation,
    newTagAssociation: TagAssociation
  ) => Promise<void>;
}

const TagContext = createContext({} as ContextState);

export const TagsContextProvider = ({ children }: React.PropsWithChildren) => {
  const { rpt } = useKeycloakAuthz();

  const client = useHttpClient("");
  const tagAPIClient = useGrpcClient(TagAPIPromiseClient);
  const accommodationAPIClient = useGrpcClient(AccommodationAPIPromiseClient);
  const extraAPIClient = useGrpcClient(ExtraAPIPromiseClient);
  const packageAPIClient = useGrpcClient(PackageAPIPromiseClient);
  const tagsCall = usePropertyGrpcCall(tagAPIClient, tagAPIClient.getTags);
  const updateTagAppliesToCall = usePropertyGrpcCall(
    tagAPIClient,
    tagAPIClient.updateTagsAppliesTo
  );
  const getAccommodationsCall = usePropertyGrpcCall(
    accommodationAPIClient,
    accommodationAPIClient.getAccommodations
  );
  const getExtrasCall = usePropertyGrpcCall(extraAPIClient, extraAPIClient.getExtras);
  const getPackagesCall = usePropertyGrpcCall(packageAPIClient, packageAPIClient.getPackages);

  const tagRepository = new ApiTagRepository(client, tagsCall);
  const tagAssociationRepository = new ApiTagAssociationRepository(
    tagRepository,
    getAccommodationsCall,
    getExtrasCall,
    getPackagesCall,
    updateTagAppliesToCall
  );

  const importTags = (propertyTicker: string, file: File) =>
    tagRepository.importTags(propertyTicker, file, rpt);

  const getTagsAssociations = (propertyTicker: string) =>
    tagAssociationRepository.getAll(propertyTicker);

  const getTags = () => tagRepository.getAll();

  const updateTagAssociation = (
    oldTagAssociation: TagAssociation,
    newTagAssociation: TagAssociation
  ) => tagAssociationRepository.modify(oldTagAssociation, newTagAssociation);

  return (
    <TagContext.Provider value={{ importTags, getTagsAssociations, getTags, updateTagAssociation }}>
      {children}
    </TagContext.Provider>
  );
};

export const useTagsContext = () => useContext(TagContext);
